/**
 * <tk-upsell-recos> — AI doporučení pro upsell bloky (PDP buybox / košík / drawer).
 *
 * Fetchuje Shopify Product Recommendations API přes section rendering (prices
 * server-rendered → EUR market safe), vykreslí kostru (skeleton) během načítání,
 * a u košíku/draweru se PŘESEEDUJE na právě přidaný produkt (cart:update event),
 * takže doporučení reagují na poslední přidanou položku — nezávisle na pořadí
 * položek v košíku (které se prependuje, viz cart-drawer.js).
 *
 * Atributy:
 *   id="tk-upsell-recos-{sectionId}"  — musí matchovat element ve fetchnuté sekci
 *   data-url        — routes.product_recommendations_url + '?limit=N'
 *   data-section-id — hostující sekce (fetch target)
 *   data-product-id — seed produkt
 *   data-intent     — 'related' | 'complementary'
 *   data-limit, data-layout, data-context, data-heading
 *   data-reseed     — když přítomno, reseeduje se na cart:update.productId
 */
(() => {
  const tkCountry = () =>
    window.__tkCountry || (window.Shopify && window.Shopify.country) || '';

  const skeletonCards = (n, layout) => {
    let s = '';
    for (let i = 0; i < n; i++) s += '<div class="tk-upsell-card tk-upsell-card--skeleton" aria-hidden="true"></div>';
    return s;
  };

  const wrapClass = (layout) =>
    layout === 'compact'
      ? 'tk-upsell__list'
      : layout === 'icons'
        ? 'tk-upsell__icons'
        : layout === 'mini'
          ? 'tk-upsell__minirow'
          : 'tk-upsell__scroll';

  class TkUpsellRecos extends HTMLElement {
    #io = null;
    #ctrl = null;
    #seed = null;
    #busy = false;

    connectedCallback() {
      this.#seed = this.dataset.productId || null;
      // SSR už může mít skeleton uvnitř; když ne, vykresli ho
      if (!this.innerHTML.trim()) this.renderSkeleton();

      // Bez lazy gate — fetchujeme doporučení HNED při připojení (uživatel chce
      // „podobné produkty" co nejrychleji, ne až na scroll do viewportu).
      this.load();
    }

    disconnectedCallback() {
      this.#io?.disconnect();
      this.#ctrl?.abort();
    }

    renderSkeleton() {
      const n = parseInt(this.dataset.limit || '4', 10);
      const layout = this.dataset.layout || 'carousel';
      const ctx = this.dataset.context || 'cart';
      const heading = this.dataset.heading
        ? `<h3 class="tk-upsell__heading">${this.dataset.heading}</h3>`
        : '';
      this.innerHTML =
        `<div class="tk-upsell tk-upsell--${ctx} tk-upsell--${layout} tk-upsell--loading">` +
        heading +
        `<div class="${wrapClass(layout)}">${skeletonCards(n, layout)}</div></div>`;
    }

    /** Reseeduj na nový produkt (po přidání do košíku) a refetchni. */
    reseed(productId) {
      if (!productId) return;
      const pid = String(productId);
      if (pid === this.#seed && !this.classList.contains('tk-upsell-recos--hidden')) return;
      this.#seed = pid;
      this.dataset.productId = pid;
      this.classList.remove('tk-upsell-recos--hidden');
      this.renderSkeleton();
      this.load();
    }

    async load() {
      const pid = this.dataset.productId;
      if (!pid || this.#busy) return;
      this.#busy = true;
      const country = tkCountry();
      const url =
        `${this.dataset.url}&product_id=${pid}&section_id=${this.dataset.sectionId}` +
        `&intent=${this.dataset.intent || 'related'}${country ? '&country=' + country : ''}`;

      this.#ctrl?.abort();
      this.#ctrl = new AbortController();
      try {
        const r = await fetch(url, { signal: this.#ctrl.signal });
        if (!r.ok) throw new Error(`status ${r.status}`);
        const text = await r.text();
        const holder = document.createElement('div');
        holder.innerHTML = text;
        const fresh = holder.querySelector(`tk-upsell-recos#${CSS.escape(this.id)}`);
        const inner = fresh ? fresh.innerHTML.trim() : '';
        if (inner) {
          this.innerHTML = inner;
        } else {
          this.classList.add('tk-upsell-recos--hidden');
        }
      } catch (e) {
        if (e?.name !== 'AbortError') this.classList.add('tk-upsell-recos--hidden');
      } finally {
        this.#busy = false;
      }
    }
  }

  if (!customElements.get('tk-upsell-recos')) {
    customElements.define('tk-upsell-recos', TkUpsellRecos);
  }

  // ── Tap-tooltip pro MINI dlaždice na touch zařízeních ──
  // Desktop ukazuje název+cenu na :hover; na touch hover není → první tap na
  // thumbnail tooltip OTEVŘE (.tip-open, preventne navigaci), druhý tap (nebo
  // tap přímo na tooltip) naviguje na produkt. Tap kamkoli jinam tooltipy zavře.
  // Delegace na document → přežívá morph/refetch lišt.
  const tkHoverless = window.matchMedia('(hover: none)');
  document.addEventListener('click', (e) => {
    // tap na otevřený tooltip → detail produktu
    const tip = e.target.closest('.tk-upsell-card__tip');
    if (tip) {
      const tipCard = tip.closest('.tk-upsell-card--mini');
      if (tipCard?.classList.contains('tip-open') && tipCard.dataset.url) {
        window.location.href = tipCard.dataset.url;
      }
      return;
    }

    const media = e.target.closest('.tk-upsell-card--mini .tk-upsell-card__mini-media');
    const card = media ? media.closest('.tk-upsell-card--mini') : null;

    // zavřít ostatní otevřené tooltipy (i při tapu mimo lišty)
    document.querySelectorAll('.tk-upsell-card--mini.tip-open').forEach((c) => {
      if (c !== card) c.classList.remove('tip-open');
    });

    if (!card || !tkHoverless.matches) return;

    if (!card.classList.contains('tip-open')) {
      // první tap: jen ukázat tooltip
      e.preventDefault();
      card.classList.add('tip-open');
    }
    // druhý tap (tip-open) → link naviguje normálně
  });

  // Reseed na právě přidaný produkt. cart:update (CartAddEvent) nese
  // detail.data.productId; qty změny ho nemají → tam nereseedujeme.
  let reseedTimer = null;
  document.addEventListener('cart:update', (e) => {
    const pid = e?.detail?.data?.productId;
    if (!pid) return;
    clearTimeout(reseedTimer);
    reseedTimer = setTimeout(() => {
      // query fresh — morphdom mohl elementy nahradit
      document.querySelectorAll('tk-upsell-recos[data-reseed]').forEach((el) => {
        if (typeof el.reseed === 'function') el.reseed(pid);
      });
    }, 150);
  });
})();
